import React from "react";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import PropTypes from "prop-types";
import { includes } from "lodash";

import { fetchGet } from "../../api/fetch";

import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";

import CloseIcon from "./CloseIcon";

import { loading, error, success } from "../../constants/common";

import colors from "../../assets/style/colors";

const styles = {
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
  circularProgress: {
    width: "20px !important",
    height: "20px !important",
  },
  noPhoto: {
    border: "1px solid " + colors.darkGrey,
    margin: "auto",
    backgroundColor: colors.mediumGrey,
    overflow: "hidden",
  },
};

const Picture = (props) => {
  const { classes, url, alt, maxHeight, style, noZoom, justify } = props;
  const [status, setStatus] = React.useState(loading);
  const [isZoomed, setIsZoomed] = React.useState(false);
  const [image, setImage] = React.useState();
  const [fullSizeImage, setFullSizeImage] = React.useState();
  const abort = React.useRef(false);

  let width = props.width;
  if (!width && !maxHeight) {
    width = 120;
  }

  React.useEffect(() => {
    abort.current = false;
    if (!props.local) {
      setFullSizeImage();
      downloadImage(true);
    }
    return () => {
      abort.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  React.useEffect(() => {
    abort.current = false;
    if (!props.local && !fullSizeImage && isZoomed) {
      downloadImage(false);
    }
    return () => {
      abort.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isZoomed]);

  const downloadImage = async (thumbnail) => {
    if (url) {
      setStatus(loading);
      try {
        let res = await fetchGet(getUrl(url, thumbnail));
        if (abort.current) return;
        if (thumbnail) {
          setImage(res);
        } else {
          setFullSizeImage(res);
        }
        setStatus(success);
      } catch (e) {
        console.log(e);
        if (abort.current) return;
        setStatus(error);
      }
    }
  };

  const getUrl = (url, thumbnail = false) => {
    let res = "";
    if (includes(url, "/api/images/")) {
      let index = url.indexOf("/api/images/") + 12;
      url = url.slice(index);
      res = `images/${url}`;
    } else {
      res = `images/${url}`;
    }
    if (thumbnail && (width || maxHeight) <= 71) {
      res = `${res}?size=thumbnail`;
    }
    return res;
  };

  const renderLoadingPicture = () => {
    return (
      <Box
        className={classes.loadingContainer}
        style={{
          justifyContent: "center",
          width: width || maxHeight,
          height: width || maxHeight,
          lineHeight: width || maxHeight,
          ...style,
          border: props.noLoadingBorder
            ? "none"
            : "1px solid " + colors.black10,
        }}
      >
        <CircularProgress className={classes.circularProgress} />
      </Box>
    );
  };

  const renderError = () => {
    return (
      <span style={{ fontSize: "0.875em" }} className="text-54">
        {I18n.get("error_failedToLoad")}
      </span>
    );
  };

  if (!url) {
    if (!width) width = maxHeight;
    return (
      <Box
        className={`text-54 text-center ${
          props.noPhotoDecoration ? "" : classes.noPhoto
        }`}
        style={{
          width: `${width}px`,
          height: `${width}px`,
          lineHeight: `${width}px`,
          ...style,
        }}
      >
        {width >= 70 && I18n.get("site_noPhoto")}
      </Box>
    );
  }

  return (
    <>
      {status === loading && renderLoadingPicture()}
      {status === error && renderError()}
      <span
        style={{
          display: status === success ? "flex" : "none",
          height: `${width || maxHeight}px`,
          alignItems: "center",
          justifyContent: justify ? justify : "center",
        }}
      >
        {props.local ? (
          <img
            src={url}
            alt={alt}
            style={{ ...style, cursor: noZoom ? "inherit" : "zoom-in" }}
            onClick={(e) => {
              if (!noZoom) e.stopPropagation();
              if (!noZoom) setIsZoomed(true);
            }}
            width={width && `${width}px`}
            onLoad={() => setStatus(success)}
            onError={() => setStatus(error)}
          />
        ) : (
          <img
            src={image}
            alt={alt}
            style={{ ...style, cursor: noZoom ? "inherit" : "zoom-in" }}
            onClick={(e) => {
              if (!noZoom) e.stopPropagation();
              if (!noZoom) setIsZoomed(true);
            }}
            width={width && `${width}px`}
          />
        )}
      </span>
      {isZoomed && (
        <Box
          style={{
            width: "100vw",
            height: "100vh",
            backgroundColor: colors.black87,
            zIndex: "2000",
            position: "fixed",
            top: 0,
            left: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloseIcon
            closeFunction={() => setIsZoomed(false)}
            darkMode={true}
            style={{
              position: "fixed",
              top: "20px",
              right: "20px",
            }}
          />
          <Box
            style={{
              width: "calc(100vw - 100px)",
              height: "calc(100vh - 80px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={props.local ? url : fullSizeImage}
              alt={alt}
              style={{
                cursor: "zoom-out",
                maxHeight: "100%",
                maxWidth: "100%",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setIsZoomed(false);
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

Picture.propTypes = {
  url: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.number,
  maxHeight: PropTypes.number,
  style: PropTypes.object,
  noZoom: PropTypes.bool,
  justify: PropTypes.string,
  local: PropTypes.bool,
  noLoadingBorder: PropTypes.bool,
};

export default withStyles(styles)(Picture);
